<template>
  <v-dialog
    :value="isOpen"
    max-width="800px"
    @input="$emit('close-dialog')"
  >
    <v-card class="overflow-x-hidden">
      <form @submit.prevent="onSubmit">
        <v-card-title>
          <p class="headline mb-0" style="width: 100%">
            {{ mess["dialog_request_title"] }}
          </p>

          <div
            v-if="!isEmpty(company)"
            style="padding-right: 12px; line-height: 14px"
          >
            <b
              style="font-size: 14px; line-height: 1;"
              :style="{ color: siteColors.primary }"
              class="mb-0"
            >
              {{ company.name }}
            </b>
          </div>

          <div
            v-if="downloadLink && downloadText && downloadURL"
            style="line-height: 12px"
          >
            <b style="font-size: 12px; line-height: 12px;">
              {{ downloadText }}
              <a :href="downloadURL" download>
                {{ downloadLink }}
              </a>
            </b>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container class="pa-0">
            <HeaderForm
              :form="form"
              :themes="themes"
              :company="company"
              :companies="companies"
              :currentCompany="currentCompany"
              :required="required.headerForm"
              @update:form="onUpdateForm($event)"
              @update:company="onUpdateCompany($event)"
            />

            <EMCUserForm
              v-if="isEMCUser && form.themes.request_theme_id !== 5666280"
              :form="form"
              :required="required.EMCUserForm"
              @update:form="onUpdateForm($event)"
            />

            <component
              :is="currentForm.component"
              ref="childForm"
              v-if="currentForm"
              v-bind="currentForm.props"
            />

            <v-col
              cols="12"
              class="pa-0 pb-2"
            >
              <FileInput
                v-model="form.files"
                :label="mess['dialog_base_field_files']"
                :placeholder="mess['dialog_base_field_files']"
                @change="onUploadFile"
              />
            </v-col>
          </v-container>
        </v-card-text>

        <v-card-actions style="padding: 0 24px 16px">
          <v-spacer />

          <v-btn
            color="primary"
            depressed
            dark
            @click="$emit('close-dialog')"
          >
            {{ mess["dialog_base_button_close"] }}
          </v-btn>

          <v-btn
            :loading="isLoading"
            type="submit"
            color="primary"
            depressed
            dark
          >
            {{ mess["dialog_base_button_send"] }}
          </v-btn>
        </v-card-actions>

        <v-btn
          absolute
          top
          right
          text
          icon
          @click="$emit('close-dialog')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </form>
    </v-card>

    <snackbar
      v-model="snackbar"
      :snackbar="snackbar"
      :snackbar-data="snackbarData"
    />
  </v-dialog>
</template>

<script>
import DictionaryRequestForm from "@/mixins/DictionaryRequestForm";
import { isEmpty } from "lodash";
import RequestDialogService from "@/services/requests-service/request-service-new";
import required from "./required-fields";
import notifications from "@/models/notificationTemplates";

import AddNewAccount from "./components/AddNewAccount.vue";
import BaseForm from "./components/BaseForm";
import ConveyorsForm from "@/components/dialogs/RequestDialog.new/components/ConveyorsForm";
import CrushingSparesForm from "@/components/dialogs/RequestDialog.new/components/CrushingSparesForm";
import EMCUserForm from "@/components/dialogs/RequestDialog.new/components/EMCUserForm.vue";
import FileInput from "@/components/FileInput";
import GrindingForm from "@/components/dialogs/RequestDialog.new/components/GrindingForm";
import HeaderForm from "./components/HeaderForm";
import HydrotransportForm from "@/components/dialogs/RequestDialog.new/components/HydrotransportForm";
import Snackbar from "@/components/Snackbar.vue";

import { EMCUserFields } from "@/components/dialogs/RequestDialog.new/fields-to-send";

/**
 * @namespace RequestDialog
 * @desc Корневой компонент поп-апа создания обращения.
 *
 * @vue-prop { Array } [companies=[]] - Массив объектов компаний для селекта выбора компании
 * @vue-prop {Object} [company={}] - Текущая выбранная компания. Используется только в поп-апе
 * @vue-prop { Boolean } isOpen - Состояние поп-апа (открыт/закрыт)
 * @vue-prop {Array} [themes=[]] - Массив объектов, где каждый объект представляет отдельную тему обращения.
 *
 * @vue-data { Object } [currentCompany=null] - Текущая выбранная компания. Нужно для поп-апа, который был открыт из вкладки Direct.
 * @vue-data { requestForm } [form=null] - Хранит в себе данные из полей формы. Данные из полей дочерних компонентов разных форм
 * (BaseForm, ConveyorsForm, CrushingSparesForm, GrindingForm, HydrotransportForm) записываются сюда при отправке формы, данные из шапки попадают
 * при обновлении полей в (HeaderForm).
 * @vue-data { Boolean } [isLoading=false] - Меняется на true при отправке запросы чтобы показать в кнопке отправить прелоадер и запретить возможность повторной
 * отправки формы до того как не уйдет предыдущий запрос.
 * @vue-data { Object } required - Объект объектов, где каждый объект хранит в себе массив обязательных к заполнению полей. Для каждого типа
 * формы существует свой массив обязательных к заполнению полей.
 *
 * @vue-computed { currentForm } currentForm - Возвращает объект, который соответствует определенной форме выбираемой при проверке id темы (form.themes.request_theme_id).
 * Детальнее о типе currentForm смотри в Globals/Type Definitions
 * @vue-computed { requestForm } forms - Свойство forms подготавливает объект объектов, где каждый объект является сущностью формы.
 * У сущности есть свойство fields, которое определяет как называется свойство в объекте fields, которое содержит
 * массив названий полей, которые надо отправить в запросе. Так же есть свойство props, которое содержит в себе
 * данные, которые мы хотим передать в качестве входных параметров в компонент указанный в свойтве component.
 *
 * @vue-event initForm - В этом методе инициализируем объект формы.
 * @vue-event onSubmit - Метод для подготовки данных и их отправки (больше комментариев в коде).
 * @vue-event { Object } onUpdateCompany - Метод принимает в качестве аргумента объект с компанией и записывает в this.form и this.currentCompany
 * @vue-event { Object } onUpdateForm - Метод принимает в качестве аргументов объект с value и key и по ним записывает значение в this.form
 * @vue-event resetValidator - Метод для сборса валидатора.
 */
export default {
  name: "RequestDialog",
  $_veeValidate: {
    validator: "new"
  },
  components: {
    AddNewAccount,
    BaseForm,
    ConveyorsForm,
    CrushingSparesForm,
    EMCUserForm,
    FileInput,
    GrindingForm,
    HeaderForm,
    HydrotransportForm,
    Snackbar
  },
  mixins: [DictionaryRequestForm],
  props: {
    companies: {
      type: Array,
      default: () => []
    },
    company: {
      type: Object,
      default: () => {}
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    selectedTheme: {
      type: Object,
      default: () => {}
    },
    themes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentCompany: null,
      form: null,
      isEMCUser: window.initialData?.isEMCUser,
      isLoading: false,
      required,
      snackbar: false,
      snackbarData: null
    };
  },
  computed: {
    currentForm() {
      const theme = parseInt(this.form.themes?.request_theme_id);
      const isWarrantyTheme = theme === 113434;
      const processID = parseInt(this.form?.process?.id);

      if (theme === 5666280) {
        return this.forms.addNewAccountForm;
      }

      if (processID === 113432 && isWarrantyTheme) {
        return this.forms.crushingSparesForm;
      }

      if (processID === 113435 && isWarrantyTheme) {
        return this.forms.grindingForm;
      }

      if (processID === 113441 && isWarrantyTheme) {
        return this.forms.conveyorsForm;
      }

      if (processID === 113439 && isWarrantyTheme) {
        return this.forms.hydrotransportForm;
      }

      return this.forms.baseForm;
    },
    downloadLink() {
      return this.form?.process?.params?.download?.link;
    },
    downloadText() {
      return this.form?.process?.params?.download?.text;
    },
    downloadURL() {
      return this.form?.process?.params?.download?.url;
    },
    errors() {
      return {};
    },
    forms() {
      if (!this.form) return {};

      return {
        baseForm: {
          fields: "BaseForm",
          component: "BaseForm",
          props: {
            themes: this.form.themes,
            required: required.base
          }
        },
        conveyorsForm: {
          fields: "ConveyorsForm",
          component: "ConveyorsForm",
          props: {
            process: this.form.process,
            required: required.conveyors
          }
        },
        crushingSparesForm: {
          fields:
            parseInt(this.form?.group.id) === 113433
              ? "CrushingSparesForm"
              : "CrushingArmorsAndComponentsForm",
          component: "CrushingSparesForm",
          props: {
            process: this.form.process,
            group: this.form.group,
            required: required.crushing
          }
        },
        grindingForm: {
          fields: "GrindingForm",
          component: "GrindingForm",
          props: {
            process: this.form.process,
            required: required.grinding
          }
        },
        hydrotransportForm: {
          fields: "HydrotransportForm",
          component: "HydrotransportForm",
          props: {
            process: this.form.process,
            required: required.hydrotransport
          }
        },
        addNewAccountForm: {
          fields: "AddNewAccountForm",
          component: "AddNewAccount",
          props: {
            required: required.addNewAccountForm
          }
        }
      };
    }
  },
  created() {
    this.initForm();
  },
  mounted() {
    this.$validator.localize("ru", this.dictionary);
  },
  watch: {
    selectedTheme: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.$set(this.form, "themes", value);
      }
    }
  },
  methods: {
    isEmpty,
    initForm() {
      this.form = {
        themes: {},
        process: {},
        group: {},
        articles: [],
        files: []
      };
    },
    async onSubmit() {
      try {
        /**
         * Запускаем валидацию текущего компонента и дочернего, обращаясь к его методу валидации
         * через $refs.childForm. После выполнения методов валидации в переменных будет хранится true или false.
         */
        const isParentValid = await this.$validator.validateAll();
        const isChildrenValid = await this.$refs.childForm.$validator.validateAll();

        /**
         * Если валидация прошла успешно, то начинаем подготовку данных для запроса.
         */
        if (isParentValid && isChildrenValid) {
          /**
           * Если переменная currentCompany пустая, значит компания была передана во входном параметре
           * company.
           */
          const company = this.currentCompany ?? this.company;
          /**
           * Получаем имя свойства, которое хранит в себе массив имен полей для отправки в запросе.
           */
          const { fields } = this.currentForm;
          /**
           * Получаем данные формы из дочернего компонента обращаясь к его методу
           * getForm через $refs.childForm
           */
          const getChildForm = this.$refs.childForm.getForm();
          /**
           * Объединяем данные формы текущего компонента,дочернего и выбранную компанию
           */
          const mergedForms = {
            company,
            ...this.form,
            ...getChildForm
          };

          /**
           * Передаем в конструктор класса RequestDialogService данные формы, которые мне объединили
           */
          const requestDialogService = new RequestDialogService(mergedForms);
          /**
           * Передаем в метод getPreparedData класса RequestDialog имя свойства, которое хранит в себе
           * массиво имен полей, которые надо отправить
           */
          const optionalFields = window.initialData.isEMCUser
            ? EMCUserFields
            : [];
          const preparedForm = requestDialogService.getPreparedData(
            fields,
            optionalFields
          );

          this.isLoading = true;

          const request = await requestDialogService.sendData(preparedForm);

          this.isLoading = false;
          this.$emit("update-request", {
            id: company.id,
            request
          });
          this.$emit("request-sent", notifications.success.requestSend);
          this.resetValidator();
          this.initForm();
          this.$emit("close-dialog");
        }
      } catch (error) {
        this.isLoading = false;
        this.$emit("request-sent", notifications.error.base);
        throw new Error(error);
      }
    },
    onUpdateCompany(company) {
      this.currentCompany = company;
      this.$set(this.form, "company", company);
    },
    onUpdateForm({ key, value }) {
      this.$set(this.form, key, value);
    },
    onUploadFile(file) {
      if (!file.length) {
        return;
      }

      const sizeInMb = (file[0].size / Math.pow(1024, 2)).toFixed(2);

      if (sizeInMb >= 1024) {
        this.form.files = [];
        this.snackbarData = {
          status: "error",
          message: this.mess["dialog_error_file_size"]
        };
        this.snackbar = true;
      }
    },
    resetValidator() {
      this.$validator.pause();
      this.$nextTick(() => {
        this.$validator.errors.clear();
        this.$validator.fields.items.forEach(field => field.reset());
        this.$validator.fields.items.forEach(field =>
          this.errors.remove(field)
        );
        this.$validator.reset();
        this.$validator.resume();
      });
    }
  }
};
</script>

<style lang="scss">
.request-datepicker-text-field {
  .v-input__prepend-outer {
    margin-right: 0 !important;
  }

  legend {
    min-width: 100px;
  }
}

fieldset {
  margin: 0 !important;
}

.fields-group {
  background: transparent;
  transition: background 250ms;

  &--active {
    .v-slot,
    .v-input__slot {
      background: rgba(0, 0, 0, 0.05) !important;
    }
  }
}

fieldset {
  margin: 0;
}

.col-4 .request-datepicker-text-field legend {
  max-width: 202px;
}

.request-form-tooltip {
  max-width: 500px;
}

.request-form-tooltip--img {
  padding: 0;
  border: 1px solid black;
  overflow: hidden;
  line-height: 0;
}

.request-form-tooltip__img {
  width: 250px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.request-form-tooltip.menuable__content__active {
  opacity: 1 !important;
  background: rgb(97, 97, 97);
}
</style>
